'use client';

import { SessionData } from '@auth0/nextjs-auth0/types';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import CaretIcon from './icons/CaretIcon';
import AvatarIcon from './icons/AvatarIcon';
import UIButton from 'components/buttons/UIButton';

interface AvatarButtonProps {
  session: SessionData;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  showDropdown?: boolean;
  onLogout?: () => void;
  isMobile?: boolean;
}

export default function AvatarButton({
  session,
  className = '',
  showDropdown = true,
  onLogout,
  isMobile = false,
}: AvatarButtonProps): ReactElement {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const shouldShowDropdown = showDropdown && isDropdownOpen;
  const containerRef = useRef<HTMLDivElement>(null);

  const user = session.user;
  const userName = user?.name || user?.email || 'User';

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      window.location.href = '/auth/logout';
    }
  };

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false);
    containerRef.current?.querySelector<HTMLElement>('.avatar-button')?.focus();
  }, []);

  useEffect(() => {
    if (!shouldShowDropdown) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeDropdown();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [shouldShowDropdown, closeDropdown]);

  return (
    <div className={`avatar-dropdown ${className}`} ref={containerRef}>
      {!isMobile && (
        <UIButton
          buttonType="tonal"
          className="avatar-button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          data-testid="avatar-button"
          aria-expanded={isDropdownOpen}
          aria-haspopup="true"
          aria-label={`${userName} account menu`}
          icon={<AvatarIcon />}
          label=""
        />
      )}

      {(shouldShowDropdown || isMobile) && (
        <div className="avatar-dropdown-menu" data-testid="avatar-dropdown">
          <div className="username-field">
            <p className="username">{userName}</p>
            <p className="signed-in">Signed in</p>
          </div>
          <UIButton
            buttonType="tonal"
            className="logout-btn"
            data-testid="logout"
            onClick={handleLogout}
            label="Log out"
            iconPosition="right"
            icon={<CaretIcon direction="right" size="large" />}
          />
        </div>
      )}

      {shouldShowDropdown && (
        <div
          aria-hidden="true"
          className="dropdown-overlay"
          onClick={closeDropdown}
        />
      )}
    </div>
  );
}
